import { z } from 'zod';
import { eq, sum } from '@fameacc/money';
import { BUCHUNGSTEXT_MAX_ZEICHEN } from './felder.js';
import { kuerzeAufZeichen } from './kodierung.js';
import { belegdatumFeld, tagesdatumFeld } from './satz.js';
/**
 * Die Zerlegungsregel — Chip `DX-satzbau`, Task 3, Schritte 1 bis 3.
 *
 * Spec `docs/superpowers/specs/2026-09-08-datev-export-design.md` Abschnitt
 * 4.1 (`D-19`) und 4.2 (`D-4`).
 *
 * ===========================================================================
 * DER SATZ, UM DEN ES GEHT
 * ===========================================================================
 *
 * **Ein DATEV-Buchungssatz hat genau zwei Konten. Unser `journal_entry` hat
 * `1..n` Zeilen.** Gemessen sind es 2,54 Zeilen je Buchung — und, die
 * entscheidende Messung, **null Buchungen mit mehreren Zeilen auf beiden
 * Seiten**. Daraus folgt eine Regel, die vollstaendig ist, und weil sie
 * vollstaendig ist, laesst sie sich ohne Datenbank beweisen:
 *
 * | Form | Ergebnis |
 * |---|---|
 * | genau eine Zeile je Seite | **ein** Satz |
 * | eine Zeile auf der einen, `n` auf der anderen | **`n`** Saetze gegen die eine Zeile |
 * | mehrere auf **beiden** Seiten | **abgewiesen** (`E-1012`) |
 *
 * **Der dritte Fall wird abgewiesen und nicht geraten.** Eine Zerlegung ueber
 * ein Zwischenkonto waere eine Korrektur in Warteposition: zwei Saetze, die
 * zusammen stimmen und einzeln keinen Geschaeftsvorfall abbilden.
 *
 * ---
 *
 * **Konto ist die Sollzeile, Gegenkonto die Habenzeile — immer.** Das ist
 * keine Willkuer, sondern die Leseart der Spec: Abschnitt 4.4 schreibt jede
 * der sechzehn Buchungsarten als „X **an** Y", und „Soll an Haben" ist die
 * Reihenfolge, in der ein Buchungssatz gesprochen wird. Abschnitt 4.2 zeigt
 * denselben Fall ausgeschrieben: `Umsatz 119,00 S Konto 1400 Gegenkonto
 * 8400` — `1400` ist die Sollzeile. Das Soll/Haben-Kennzeichen wird trotzdem
 * **abgeleitet** und nicht als `'S'` hingeschrieben: Wer die Paarungsregel
 * einmal aendert, soll das Kennzeichen nicht getrennt nachziehen muessen.
 *
 * ---
 *
 * **Ein Reseller ist kein eigener Weg** (Spec 4.4, Zeile 16). Er ist ein
 * Kunde mit einer anderen steuerlichen Lage, und die wirkt ueber das
 * **Erloeskonto** (`8336`/`8338`) — nicht ueber einen Sonderfall hier. Wer an
 * dieser Stelle einen Reseller-Zweig baut, baut die Steuerlogik ein zweites
 * Mal, und die zweite Fassung wird die falsche sein.
 */
/** Die Seite, auf der eine Journalzeile steht. */
export const buchungsseiteSchema = z.enum(['soll', 'haben']);
/**
 * Belegfeld 1 der BUCHUNG: Rechnungsnummer, sonst die Belegnummer des
 * Kreditors einer zugeordneten Eingangsrechnung (`E-1900`), sonst
 * Quellverweis, sonst Buchungsnummer. Sie ist der Rueckfall jedes Satzes ohne
 * eigene Nummer und der Name im Belegordner (`belege.ts`), der nur Buchungen
 * MIT `document_id` fuehrt.
 */
export function belegfeld1DerBuchung(quellen) {
    return (quellen.belegnummer ??
        quellen.eingangsbelegnummer ??
        quellen.quellreferenz ??
        quellen.buchungsnummer);
}
/**
 * Belegfeld 1 EINES Satzes (Mehrkunden-Spec 9.3, `D-18`, `D-27`, Fall
 * `E-1522`). Die Rueckfallkette, woertlich:
 *
 * 1. die Rechnungsnummer der BUCHUNG (`journal_entry.document_id`) — sie
 *    gewinnt, und eine solche Buchung bleibt, wie sie war;
 * 2. sonst die EINE Belegnummer der Zeilen dieses Satzes (Konto- und
 *    Gegenkontozeile), die eine tragen — zwei verschiedene sind ein Befund
 *    (`belegfeld_widerspruch`) und keine Wahl;
 * 3. sonst die Belegnummer des Kreditors, wenn die Kontierung der Buchung
 *    eine Eingangsrechnung zugeordnet hat (`E-1900`, DATEV-Spec 4.4, Zeile 14);
 * 4. sonst `source_reference` der Buchung (Aufladungskennung, Kennung der
 *    Bankbewegung);
 * 5. zuletzt `entry_no`.
 *
 * **Warum je Satz und nicht je Buchung.** Eine Zahlungszuordnung ueber n
 * Rechnungen bucht EINE Buchung mit n Forderungszeilen und wird im Export zu
 * n Saetzen gegen das Geldkonto. Je Buchung haette jeder dieser Saetze die
 * Kennung der Bankbewegung getragen (`source_reference`, eine UUID, die den
 * Zeichenvorrat besteht) — der Ausgleich drueben faende keine einzige
 * Rechnung. Je Satz traegt jeder die Nummer SEINER Rechnung.
 *
 * **Warum der Widerspruch nicht entschieden wird.** Der Weg dieser Aera
 * erzeugt ihn nicht: Ein Satz stellt entweder eine Zeile ohne Beleg (Geld-
 * oder Gebuehrenkonto) gegen eine Positionszeile, oder die Rundungszeile gegen
 * die Forderungszeile DESSELBEN Belegs (`2300` an `1400`, `D-6`). Tritt er
 * trotzdem auf, stimmt am Bestand etwas nicht, und eine gewaehlte Nummer
 * waere eine Korrektur des Steuerberaters mit Ansage.
 */
export function belegfeld1DesSatzes(quellen, zeilenDesSatzes) {
    if (quellen.belegnummer !== null)
        return { ok: true, belegfeld1: quellen.belegnummer };
    const nummern = [
        ...new Set(zeilenDesSatzes
            .map((zeile) => zeile.belegnummer ?? null)
            .filter((nummer) => nummer !== null)),
    ];
    if (nummern.length > 1)
        return { ok: false, nummern };
    return {
        ok: true,
        belegfeld1: nummern[0] ??
            quellen.eingangsbelegnummer ??
            quellen.quellreferenz ??
            quellen.buchungsnummer,
    };
}
/**
 * Der Buchungstext eines Satzes (`E-1021`).
 *
 * **`journal_line.description` vor `journal_entry.description`** — die Zeile
 * ist genauer als die Buchung. Von den zwei Zeilen eines Satzes ist die
 * **tragende** die genauere: diejenige, die den Betrag beigesteuert hat. Bei
 * `1:n` ist das die Zeile auf der `n`-Seite, denn sie unterscheidet die
 * Saetze voneinander; die eine Zeile auf der Gegenseite ist bei allen `n`
 * dieselbe und kann den Unterschied gar nicht tragen.
 *
 * `journal_entry.description` ist `NOT NULL` mit `btrim(...) <> ''`
 * (Migration `0003`) — es gibt also immer einen Rueckfall, und der letzte
 * Zweig dieser Funktion ist keine Notloesung.
 */
function buchungstextFuer(tragend, gegenueber, rueckfall) {
    const gewaehlt = leerZuNull(tragend.beschreibung) ?? leerZuNull(gegenueber.beschreibung) ?? rueckfall;
    return kuerzeAufZeichen(gewaehlt, BUCHUNGSTEXT_MAX_ZEICHEN);
}
/** Ein Text aus lauter Leerzeichen ist kein Buchungstext. */
function leerZuNull(text) {
    if (text === null)
        return null;
    return text.trim() === '' ? null : text;
}
/**
 * Der BU-Schluessel eines Satzes.
 *
 * Er haengt an der **steuertragenden** Zeile des Paares, und die ist nicht
 * immer die Gegenseite: Bei einer Ausgangsrechnung traegt das Erloeskonto den
 * Schluessel (Habenseite), bei einer Eingangsrechnung das Vorsteuerkonto
 * (Sollseite). Deshalb wird die Gegenseite zuerst gefragt und die Kontoseite
 * als Rueckfall — und nicht eine der beiden fest gewaehlt.
 *
 * **Auf einem Automatikkonto entfaellt er** (`D-4`): Dort rechnet DATEV die
 * Steuer selbst, und ein mitgegebener Schluessel ist ein Fehlerbild. Der Fall
 * wird gemeldet und nicht verschwiegen — blockiert wird er in Vorpruefung 12
 * (`E-1033`, Task 6), weil das eine Aussage ueber den **Bestand** ist und
 * nicht ueber die Uebersetzung.
 */
function schluesselFuer(konto, gegenkonto, hinweise) {
    if (konto.istAutomatikkonto || gegenkonto.istAutomatikkonto) {
        if (konto.buSchluessel !== null || gegenkonto.buSchluessel !== null) {
            hinweise.add('automatikkonto_schluessel_entfaellt');
        }
        return null;
    }
    return gegenkonto.buSchluessel ?? konto.buSchluessel;
}
/**
 * Baut einen Satz aus einem Paar. `tragend` ist die Zeile, die den Betrag
 * stellt; `belegfeld1` ist bereits je Satz entschieden
 * ({@link belegfeld1DesSatzes}).
 */
function baueSatz(buchung, konto, gegenkonto, tragend, betrag, belegfeld1, hinweise) {
    const sollHaben = konto.seite === 'soll' ? 'S' : 'H';
    const gegenueber = tragend === konto ? gegenkonto : konto;
    return {
        umsatz: betrag,
        sollHaben,
        konto: konto.kontonummer,
        gegenkonto: gegenkonto.kontonummer,
        buSchluessel: schluesselFuer(konto, gegenkonto, hinweise),
        belegdatum: belegdatumFeld(buchung.buchungsdatum),
        belegfeld1,
        buchungstext: buchungstextFuer(tragend, gegenueber, buchung.buchungstext),
        euLandUstId: buchung.euLandUstId,
        euSteuersatz: buchung.euSteuersatz,
        leistungsdatum: buchung.leistungsdatum === null ? null : tagesdatumFeld(buchung.leistungsdatum),
        // Feld 116 ist Pflicht, sobald Feld 115 steht. Gewaehlt ist das
        // **Buchungsdatum** und nicht der Leistungsstichtag: Die Zuordnung zur
        // Steuerperiode ist die Aussage, in welchen Voranmeldungszeitraum der
        // Satz faellt, und das ist bei einer ist-versteuernden Buchfuehrung die
        // Periode, in der gebucht wurde. Sie deckt sich damit zwangslaeufig mit
        // dem Kopfzeitraum des Stapels, und ein Satz kann seine eigene Periode
        // nicht verlassen. Der Leistungsstichtag darf das sehr wohl — genau
        // deshalb sind es zwei Felder und nicht eines.
        datumSteuerperiode: buchung.leistungsdatum === null ? null : tagesdatumFeld(buchung.buchungsdatum),
    };
}
function abweisung(grund, meldung) {
    return { ok: false, grund, meldung };
}
/** Die Abweisung, wenn die zwei Zeilen eines Satzes verschiedene Belegnummern tragen. */
function widerspruch(konto, gegenkonto, nummern) {
    return abweisung('belegfeld_widerspruch', `Der Satz aus Zeile ${String(konto.position)} und Zeile ${String(gegenkonto.position)} ` +
        `traegt die Belegnummern ${nummern.join(' und ')}. Belegfeld 1 ist OPOS-tragend; ` +
        `eine gewaehlte Nummer waere eine Korrektur des Steuerberaters (Mehrkunden-Spec 9.3, E-1522).`);
}
/**
 * Zerlegt eine Journalbuchung in einen oder mehrere Buchungssaetze.
 *
 * Die Reihenfolge der Saetze ist die Positionsreihenfolge der Zeilen auf der
 * mehrfach besetzten Seite. Sie ist damit bestimmt, und `D-24` (byteidentisch
 * aus demselben Stand) haengt daran.
 */
export function zerlegeBuchung(buchung) {
    const soll = buchung.zeilen.filter((zeile) => zeile.seite === 'soll');
    const haben = buchung.zeilen.filter((zeile) => zeile.seite === 'haben');
    if (soll.length === 0 || haben.length === 0) {
        return abweisung('seite_fehlt', `Die Buchung hat ${soll.length} Sollzeile(n) und ${haben.length} Habenzeile(n). ` +
            `Ein Buchungssatz braucht beide Seiten.`);
    }
    const sollSumme = sum(soll.map((zeile) => zeile.betrag));
    const habenSumme = sum(haben.map((zeile) => zeile.betrag));
    if (!eq(sollSumme, habenSumme)) {
        return abweisung('unausgeglichen', `Soll ${sollSumme} und Haben ${habenSumme} stimmen nicht ueberein. Eine ` +
            `unausgeglichene Buchung ist kein Uebersetzungsfall, sondern ein Befund im Journal.`);
    }
    if (soll.length > 1 && haben.length > 1) {
        return abweisung('mehrere_auf_beiden_seiten', `Die Buchung hat ${soll.length} Sollzeilen und ${haben.length} Habenzeilen. ` +
            `Es gibt kein eindeutiges Gegenkonto, und eine Zerlegung ueber ein Zwischenkonto ` +
            `waere eine Korrektur in Warteposition: zwei Saetze, die zusammen stimmen und ` +
            `einzeln keinen Geschaeftsvorfall abbilden (D-19, E-1012).`);
    }
    const hinweise = new Set();
    const einzeln = soll.length === 1 ? soll[0] : haben[0];
    const mehrere = soll.length === 1 ? haben : soll;
    if (einzeln === undefined) {
        return abweisung('seite_fehlt', 'Die einfach besetzte Seite ist leer.');
    }
    const paarung = (gegenstueck) => einzeln.seite === 'soll'
        ? { konto: einzeln, gegenkonto: gegenstueck }
        : { konto: gegenstueck, gegenkonto: einzeln };
    // ---------------------------------------------------------------------
    // Der Bruttofall: die dreizeilige Rechnung ergibt EINEN Satz (D-4, E-1010)
    // ---------------------------------------------------------------------
    //
    // 1400 Soll 119,00 / 8400 Haben 100,00 / 1766 Haben 19,00 waere nach der
    // reinen Zaehlregel ein 1:2 und ergaebe zwei Saetze. Es ergibt einen:
    // `8400` ist ein Automatikkonto, DATEV rechnet die Steuer dort selbst, und
    // die `1766`-Zeile geht NICHT als eigener Satz hinaus (E-1013) — sie geht
    // als Summe ins Nachweisblatt.
    //
    // Erkannt wird der Fall an der Form und nicht an Kontonummern: genau ein
    // Automatikkonto auf der mehrfach besetzten Seite, alles uebrige dort ist
    // Steuerkonto. Eine Gebuehrenzeile (`4970`) ist keins von beidem, und
    // deshalb faellt eine Zahldienstbuchung hier nicht herein.
    const automatikzeilen = mehrere.filter((zeile) => zeile.istAutomatikkonto);
    const steuerzeilen = mehrere.filter((zeile) => zeile.istSteuerkonto && !zeile.istAutomatikkonto);
    const automatik = automatikzeilen[0];
    if (mehrere.length > 1 &&
        automatikzeilen.length === 1 &&
        automatik !== undefined &&
        steuerzeilen.length === mehrere.length - 1) {
        const { konto, gegenkonto } = paarung(automatik);
        const feld = belegfeld1DesSatzes(buchung.belegfeld1, [konto, gegenkonto]);
        if (!feld.ok)
            return widerspruch(konto, gegenkonto, feld.nummern);
        hinweise.add('steuerzeile_ohne_eigenen_satz');
        return {
            ok: true,
            form: 'brutto_automatik',
            saetze: [
                baueSatz(buchung, konto, gegenkonto, einzeln, einzeln.betrag, feld.belegfeld1, hinweise),
            ],
            ohneEigenenSatz: steuerzeilen,
            hinweise: [...hinweise],
        };
    }
    // ---------------------------------------------------------------------
    // Der zweite Bruttofall: Steuerzeile hinter einer Zeile MIT Schluessel
    // ---------------------------------------------------------------------
    //
    // `D-K8` der Sachkonten-Spec (Migration `0290`, `E-1823`): Seit eine
    // Kontierung mit Steuerschluessel ihre Steuerzeile bucht, steht dieselbe
    // Form auf einem Konto OHNE Automatik -- `4210` Soll 720,00 (`9`) / `1576`
    // Soll 136,80 (`9`) / `1200` Haben 856,80. Auch das ergibt EINEN Satz ueber
    // das Brutto, `4210` an `1200` 856,80, mit dem BU-Schluessel der tragenden
    // Zeile: DATEV rechnet die Vorsteuer aus dem Schluessel selbst (DATEV-Spec
    // 4.4, Zeile 14), und die `1576`-Zeile geht wie die `1766`-Zeile ins
    // Nachweisblatt. Zwei Saetze waeren die doppelte Vorsteuer: einer mit
    // Schluessel auf 720,00 (DATEV rechnete 114,96 heraus) und einer auf dem
    // Steuerkonto.
    //
    // Erkannt an der Form, wie oben: Kein Automatikkonto auf der Seite, und
    // JEDE Steuerzeile folgt unmittelbar einer Zeile, die weder Steuer- noch
    // Automatikkonto ist und DENSELBEN BU-Schluessel traegt -- genau die
    // Reihenfolge, in der `kontierung/buchung.ts::baueKontierungsbuchung()` je
    // Position Sachkonto- und Steuerzeile schreibt. Eine Kontierung mit zwei
    // besteuerten Positionen ergibt so zwei Bruttosaetze; eine Steuerzeile, die
    // sich keiner Zeile zuordnen laesst, faellt in die Zaehlregel unten und
    // bleibt sichtbar, statt geraten zu werden.
    if (automatikzeilen.length === 0 && steuerzeilen.length > 0) {
        const gruppen = [];
        let zuordenbar = true;
        for (const zeile of mehrere) {
            if (!zeile.istSteuerkonto) {
                gruppen.push({ tragend: zeile, steuer: [] });
                continue;
            }
            const vorige = gruppen[gruppen.length - 1];
            if (vorige === undefined ||
                vorige.steuer.length > 0 ||
                vorige.tragend.buSchluessel === null ||
                vorige.tragend.buSchluessel !== zeile.buSchluessel) {
                zuordenbar = false;
                break;
            }
            vorige.steuer.push(zeile);
        }
        if (zuordenbar) {
            const bruttosaetze = [];
            for (const gruppe of gruppen) {
                const { konto, gegenkonto } = paarung(gruppe.tragend);
                const feld = belegfeld1DesSatzes(buchung.belegfeld1, [konto, gegenkonto]);
                if (!feld.ok)
                    return widerspruch(konto, gegenkonto, feld.nummern);
                const betrag = sum([gruppe.tragend.betrag, ...gruppe.steuer.map((s) => s.betrag)]);
                bruttosaetze.push(baueSatz(buchung, konto, gegenkonto, gruppe.tragend, betrag, feld.belegfeld1, hinweise));
            }
            hinweise.add('steuerzeile_ohne_eigenen_satz');
            return {
                ok: true,
                form: 'brutto_schluessel',
                saetze: bruttosaetze,
                ohneEigenenSatz: steuerzeilen,
                hinweise: [...hinweise],
            };
        }
    }
    // Belegfeld 1 je SATZ (Mehrkunden-Spec 9.3, E-1522): Ein Zahlungseingang
    // ueber n Rechnungen wird zu n Saetzen mit n Rechnungsnummern.
    const saetze = [];
    for (const zeile of mehrere) {
        const { konto, gegenkonto } = paarung(zeile);
        const feld = belegfeld1DesSatzes(buchung.belegfeld1, [konto, gegenkonto]);
        if (!feld.ok)
            return widerspruch(konto, gegenkonto, feld.nummern);
        saetze.push(baueSatz(buchung, konto, gegenkonto, zeile, zeile.betrag, feld.belegfeld1, hinweise));
    }
    return {
        ok: true,
        form: mehrere.length === 1 ? 'einzelsatz' : 'eins_zu_n',
        saetze,
        ohneEigenenSatz: [],
        hinweise: [...hinweise],
    };
}
//# sourceMappingURL=zerlegung.js.map